import { CloseIcon } from 'assets/icons/CloseIcon'
import { CancelBtn } from 'components/Button/CancelBtn'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { Input } from 'components/Input/Input'
import { NavBar } from 'components/NavBar/NavBar'
import { ISelectValue } from 'constants/globalTypes'
import { addBreakPolicy } from 'features/breaks/breaksSlice'
import { parseLocationRegistryOptions } from 'features/locationRegistry/locationRegistryMappers'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AppDispatch } from 'store'
import { Button, Flex, H1WithBack, ItemsGrid, PageFooter, PageWrapper, ThinSeparator } from 'styling/GlobalStyles'
import { OtherText, TitleText } from 'styling/Typography'
import {
  BreakPolicyScopeType,
  breakPolicyAnchorTypeOptions,
  breakPolicyEnforcementTypeOptions,
  breakPolicyPlacementStrategyOptions,
  breakPolicyScopeTypeOptions,
  emptyBreakPolicyState,
  IBreakPolicyState,
} from 'types/breakPolicy.interface'

export const AddBreakPolicy = () => {
  const [state, setState] = useState<IBreakPolicyState>(emptyBreakPolicyState())
  const [breakTemplateBreakOptions, setOptionsForBreakTemplateBreakOptions] = useState<ISelectValue[]>([])
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()

  const handleSelect = (v: ISelectValue, name: string) => {
    if (name == 'breakTemplate') {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //  @ts-ignore
      setOptionsForBreakTemplateBreakOptions(v.breaks.map((b) => ({ value: b.id, label: b.durationMinutes + ' min.' })))
    }
    if (name == 'scopeType') {
      setState((prev) => ({
        ...prev,
        scopeType: v,
        siteNode: v.value === BreakPolicyScopeType.Site ? prev.siteNode : null,
      }))
      return
    }
    setState((prev) => ({ ...prev, [name]: v }))
  }

  const handleDate = (date: Date, name: string) => {
    setState((prev) => ({ ...prev, [name]: date }))
  }

  const validate = () => {
    if (!state.breakTemplate?.value) {
      toast.warn('Break template must be selected')
      return false
    }
    if (state.scopeType?.value === BreakPolicyScopeType.Site && !state.siteNode?.value) {
      toast.warn('Site must be selected for a site-scoped break policy')
      return false
    }
    if (!state.roleRules?.length) {
      toast.warn('At least one role rule is required')
      return false
    }
    if (state.roleRules.some((r) => !r.role?.value || !r.minimumActiveCount || r.minimumActiveCount < 1)) {
      toast.warn('Each role rule needs a role and a minimum active count of at least 1')
      return false
    }
    const roleIds = state.roleRules.map((r) => r.role?.value)
    if (new Set(roleIds).size !== roleIds.length) {
      toast.warn('Each role can only appear once in role rules')
      return false
    }
    return true
  }

  const handleSubmit = async () => {
    if (!validate()) return
    const resp = await dispatch(addBreakPolicy(state))
    if (resp.meta.requestStatus === 'fulfilled') {
      navigate(-1)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, valueAsNumber } = e.target
    setState((prev) => ({ ...prev, [name]: valueAsNumber }))
  }

  const parseMaybeNumber = (raw: string): number | null => {
    if (raw === '') return null
    const num = Number(raw)
    return Number.isNaN(num) ? null : num
  }

  const addRoleRule = () => {
    setState((prev) => ({
      ...prev,
      roleRules: [...prev.roleRules, { role: null, minimumActiveCount: null }],
    }))
  }

  const updateRoleRule = (index: number, name: string, value: any) => {
    setState((prev) => ({
      ...prev,
      roleRules: prev.roleRules.map((r, i) => (i === index ? { ...r, [name]: value } : r)),
    }))
  }

  const deleteRoleRule = (index: number) => {
    setState((prev) => {
      if (prev.roleRules.length <= 1) return prev
      return { ...prev, roleRules: prev.roleRules.filter((_, i) => i !== index) }
    })
  }

  const addConstraint = () => {
    if (!breakTemplateBreakOptions.length) {
      toast.warn('Break template must be selected and it must contain breaks')
      return
    }
    setState((prev) => ({
      ...prev,
      constraints: [
        ...prev.constraints,
        {
          id: new Date().getTime(),
          breakTemplateBreak: null,
          index: prev.constraints.length + 1,
          anchorType: null,
          minOffsetMinutes: null,
          maxOffsetMinutes: null,
          placementStrategyType: null,
          durationOverrideMinutes: null,
          enforcementType: null,
        },
      ],
    }))
  }

  const updateConstraint = (constraintIndex: number, name: string, value: any) => {
    setState((prev) => ({
      ...prev,
      constraints: prev.constraints
        .map((c, i) => (i === constraintIndex ? { ...c, [name]: value } : c))
        .map((c, i) => ({ ...c, index: i + 1 })),
    }))
  }

  const deleteConstraint = (constraintIndex: number) => {
    setState((prev) => ({
      ...prev,
      constraints: prev.constraints
        .filter((_, i) => i !== constraintIndex)
        .map((c, i) => ({ ...c, index: i + 1 })),
    }))
  }

  const parseRoles = (data: any) => data.data.map((item: any) => ({ label: item.name, value: item.id }))

  return (
    <>
      <NavBar />
      <PageWrapper>
        <H1WithBack arrow title='Add Break Policy' />
        <ItemsGrid mt='16px' columns={4}>
          <HonestSelectAsyncInput
            required
            labelText='Break Template'
            nomarg
            defaultValue={state.breakTemplate?.label}
            onChange={handleSelect}
            name='breakTemplate'
            link='break/template'
            parserFunction={(data) =>
              data.data.map((p) => ({ value: p.id, breaks: p.breaks, label: p.name }))
            }
          />
          <HonestSelectInput
            required
            options={breakPolicyScopeTypeOptions}
            nomarg
            labelText='Scope type'
            defaultValue={state.scopeType?.value}
            onChange={handleSelect}
            name='scopeType'
          />
          {state.scopeType?.value === BreakPolicyScopeType.Site && (
            <HonestSelectAsyncInput
              required
              nomarg
              labelText='Site'
              name='siteNode'
              link='locations'
              additionalParams={{ type: 1 }}
              defaultValue={state.siteNode?.label}
              onChange={handleSelect}
              parserFunction={parseLocationRegistryOptions}
            />
          )}
          <Input
            value={state.maxOnBreak}
            required
            nomarg
            labelText='Max on break'
            name='maxOnBreak'
            type='number'
            placeholder='Enter here...'
            onChange={handleChange}
          />
          <Input
            value={state.minActive}
            required
            nomarg
            labelText='Minimum active'
            name='minActive'
            type='number'
            placeholder='Enter here...'
            onChange={handleChange}
          />
          <Input
            value={state.minGapBetweenBreaksInMinutes}
            required
            nomarg
            labelText='Minumum gap between breaks (min)'
            name='minGapBetweenBreaksInMinutes'
            type='number'
            placeholder='Enter here...'
            onChange={handleChange}
          />
          <CalendarInput
            maxDate={state.validToDate}
            minDate={new Date()}
            nomarg
            name='validFromDate'
            labelText='Valid From'
            onChange={handleDate}
            defaultValue={state.validFromDate}
          />
          <CalendarInput
            minDate={state.validFromDate}
            nomarg
            labelText='Valid To'
            name='validToDate'
            onChange={handleDate}
            defaultValue={state.validToDate}
          />
        </ItemsGrid>

        <ThinSeparator />
        <Flex column gap='1rem' mt='16' width='100%'>
          <Flex between center mb='16'>
            <Flex column style={{ justifyContent: 'center' }}>
              <TitleText weight='bold'>Role rules</TitleText>
              <OtherText grey>
                Per-role minimum active counts applied inside this policy&apos;s scope group.
              </OtherText>
            </Flex>
            <Button orange onClick={addRoleRule}>
              Add Role Rule
            </Button>
          </Flex>
          {state.roleRules.map((rule, idx) => (
            <Flex key={idx} gap='1rem' center>
              <HonestSelectAsyncInput
                required
                nomarg
                labelText='Role'
                name='role'
                link='roles'
                defaultValue={rule.role?.label}
                onChange={(v) => updateRoleRule(idx, 'role', v)}
                parserFunction={parseRoles}
              />
              <Input
                required
                nomarg
                labelText='Minimum active count'
                type='number'
                value={rule.minimumActiveCount}
                placeholder='1'
                onChange={(e) =>
                  updateRoleRule(idx, 'minimumActiveCount', parseMaybeNumber(e.target.value))
                }
              />
              <div style={{ width: 50 }}>
                {state.roleRules.length > 1 && (
                  <CloseIcon onClick={() => deleteRoleRule(idx)} />
                )}
              </div>
            </Flex>
          ))}
        </Flex>

        <ThinSeparator />
        <Flex column gap='1rem' mt='16' width='100%'>
          <Flex between center mb='16'>
            <Flex column style={{ justifyContent: 'center' }}>
              <TitleText weight='bold'>Constraints</TitleText>
            </Flex>
            <Button orange onClick={addConstraint} disabled={!breakTemplateBreakOptions.length}>
              Add Constraint
            </Button>
          </Flex>

          {state.constraints.map((constraint, idx) => (
            <Flex key={idx} gap='1rem' center>
              <HonestSelectInput
                required
                nomarg
                labelText='Break'
                options={breakTemplateBreakOptions}
                defaultValue={constraint.breakTemplateBreak?.value}
                onChange={(v) => updateConstraint(idx, 'breakTemplateBreak', v)}
                name='breakTemplateBreak'
                disabled={!breakTemplateBreakOptions.length}
              />
              <HonestSelectInput
                required
                nomarg
                labelText='Anchor'
                options={breakPolicyAnchorTypeOptions}
                defaultValue={constraint.anchorType?.value}
                onChange={(v: ISelectValue) => updateConstraint(idx, 'anchorType', v)}
                name='anchorType'
              />
              <Input
                allowZero
                required
                nomarg
                labelText='Min offset (min)'
                type='number'
                value={constraint.minOffsetMinutes}
                placeholder='Enter here...'
                onChange={(e) =>
                  updateConstraint(idx, 'minOffsetMinutes', parseMaybeNumber(e.target.value))
                }
              />
              <Input
                required
                allowZero
                nomarg
                labelText='Max offset (min)'
                type='number'
                value={constraint.maxOffsetMinutes}
                placeholder='Enter here...'
                onChange={(e) =>
                  updateConstraint(idx, 'maxOffsetMinutes', parseMaybeNumber(e.target.value))
                }
              />
              <HonestSelectInput
                required
                nomarg
                labelText='Placement'
                options={breakPolicyPlacementStrategyOptions}
                defaultValue={constraint.placementStrategyType?.value}
                onChange={(v) => updateConstraint(idx, 'placementStrategyType', v)}
                name='placementStrategyType'
              />
              <Input
                allowZero
                required
                nomarg
                labelText='Duration override (min)'
                type='number'
                value={constraint.durationOverrideMinutes}
                placeholder='Enter here...'
                onChange={(e) =>
                  updateConstraint(idx, 'durationOverrideMinutes', parseMaybeNumber(e.target.value))
                }
              />
              <HonestSelectInput
                required
                nomarg
                labelText='Enforcement'
                options={breakPolicyEnforcementTypeOptions}
                defaultValue={constraint.enforcementType?.value}
                onChange={(v) => updateConstraint(idx, 'enforcementType', v as ISelectValue)}
                name='enforcementType'
              />
              <div style={{ width: 50 }}>
                <CloseIcon onClick={() => deleteConstraint(idx)} />
              </div>
            </Flex>
          ))}
        </Flex>
      </PageWrapper>
      <PageFooter>
        <Flex width='100%' end='true'>
          <CancelBtn />
          <Button ml='15' height='40' orange onClick={handleSubmit}>
            Save
          </Button>
        </Flex>
      </PageFooter>
    </>
  )
}

export const scopeTypeOptions = breakPolicyScopeTypeOptions
export const anchorTypeOptions = breakPolicyAnchorTypeOptions
export const placementStraregyOptions = breakPolicyPlacementStrategyOptions
export const enforcementTypeOptions = breakPolicyEnforcementTypeOptions
